import { Injectable } from "@angular/core";
import { APIService } from "./api.service";
import { StorageService } from "./storage.service";
import { Decimal } from 'decimal.js';
import { PusherService } from "./pusher.service";
import { Subject } from 'rxjs';
import { ObserverService } from "./observer.service";

interface Payment {
    price: number;
    surcharge_rate?: number;
    method: "credit_card" | "eftpos";
    label: "Credit card" | "EFTPOS";
    payment_service_transaction_id: string;
    payment_service_receipt_text: string;
};

interface PaymentStatus {
    transaction_id: string;
    status: "declined" | "failed" | "success" | "delayed" | "cancelled" | "offline" | "service_unavailable" | "register_not_paired" | "reader_busy";
    payment?: Payment;
}

@Injectable({
	providedIn: "root",
})
export class SmartpayReaderService {
    polling_interval_id = null;
    processing_charge = false;
    polling_url = null;
    show_possibly_offline = false;
    payment = {};
    current_transaction_id = null;
    current_register_id = null;

    private transactionSubject = new Subject<PaymentStatus>();
    transactionObservable = this.transactionSubject.asObservable();

	constructor(
		private apiService: APIService,
		private storageService: StorageService,
        private pusherService: PusherService,
        private observerService: ObserverService
	) { }

    init() { // Called in app component after pusher service has been initialised
        this.pusherService.pusher_channel.bind("smartpay_callback", (callback_data) => {
            if(callback_data && callback_data.transaction_id == this.current_transaction_id && this.processing_charge) {
                this.pollTransaction(this.polling_url);
            }
        });
        this.storageService.get("register_id").then((register_id) => {
            this.current_register_id = register_id;
        });
        this.observerService.subscribe('register_id_changed', () => {
            this.storageService.set("smartpay_reader", false);
        });
    }

    clearCurrentTransaction() {
        this.current_transaction_id = null;
        this.polling_url = null;
        this.processing_charge = false;
        clearInterval(this.polling_interval_id);
    }
   
    pollTransaction(polling_url) {
        if(!this.processing_charge){
            // Already handled
            return;
        }
        if(!polling_url) {
            // No polling url
            return;
        }
        fetch(polling_url).then((response) => {
            response.json().then((data) => {
                if(data && data.transactionStatus) {

                    const transaction_status = data.transactionStatus;
                    const transaction_result = data.data.TransactionResult;
                    const transaction_id = data.transactionId;
                    const data_result = data.data.Result;

                    if(transaction_status == "PENDING"){
                        if(transaction_result == "OK-DELAYED" && data_result == "DELAYED-TRANSACTION"){
                            this.transactionSubject.next({
                                transaction_id: transaction_id,
                                status: "delayed"
                            });
                        }
                        return;
                    }
                    else if(transaction_status == "COMPLETED") {
                        this.clearCurrentTransaction();
                        if(transaction_result == "OK-ACCEPTED" && data_result == "OK"){
                            let payment_surcharge_rate: number, payment_method: 'eftpos' | 'credit_card', payment_label: 'EFTPOS' | 'Credit card';
                            let original_payment_price = Number(new Decimal(data.data.AmountTotal).dividedBy(100).toDecimalPlaces(2));
                            let payment_price = original_payment_price;
                            let tip_price: number;
                            if("AmountTip" in data.data) {
                                tip_price = Number(new Decimal(data.data.AmountTip).dividedBy(100).toDecimalPlaces(2));
                                payment_price = Number(new Decimal(payment_price).minus(new Decimal(tip_price)).toDecimalPlaces(2));   
                            }
                            if("AmountSurcharge" in data.data){
                                let surcharge_price = Number(new Decimal(data.data.AmountSurcharge).dividedBy(100).toDecimalPlaces(2));
                                if("AmountTip" in data.data) {
                                    let original_payment_without_surcharge_price = Number(new Decimal(original_payment_price).minus(surcharge_price).toDecimalPlaces(2));
                                    // Calculate tip's surcharge based on tip's proportion of the original payment
                                    let tip_surcharge_price = Number(new Decimal(surcharge_price).times(tip_price).dividedBy(original_payment_without_surcharge_price).toDecimalPlaces(2));
                                    payment_price = Number(new Decimal(payment_price).minus(tip_surcharge_price).toDecimalPlaces(2));
                                    surcharge_price = Number(new Decimal(surcharge_price).minus(tip_surcharge_price).toDecimalPlaces(2));
                                }
                                const price_without_surcharge = Number(new Decimal(payment_price).minus(surcharge_price).toDecimalPlaces(2));
                                // * 100 to get rate as a percentage
                                payment_surcharge_rate = Number(new Decimal(surcharge_price).dividedBy(price_without_surcharge).times(100).toDecimalPlaces(4));
                            }
                            if(data.data.CardType == "EFTPOS DEBIT" && data.data.AccountType != "CREDIT") {
                                payment_method = "eftpos";
                                payment_label = "EFTPOS";
                            } else {
                                payment_method = "credit_card";
                                payment_label = "Credit card";    
                            }
                            const payment: Payment = {
                                price: payment_price,
                                surcharge_rate: payment_surcharge_rate,
                                method: payment_method,
                                label: payment_label,
                                payment_service_transaction_id: transaction_id,
                                payment_service_receipt_text: data.data.Receipt
                            };
                            this.transactionSubject.next({
                                transaction_id: transaction_id,
                                status: "success",
                                payment: payment
                            });
                            return;
                        }
                        else if(transaction_result == "OK-DECLINED" && data_result == "OK"){
                            this.transactionSubject.next({
                                transaction_id: transaction_id,
                                status: "declined"
                            });
                            return;
                        }
                        else if(transaction_result == "CANCELLED"){
                            if(data.data.Result == "FAILED-INTERFACE"){
                                this.transactionSubject.next({
                                    transaction_id: transaction_id,
                                    status: "offline"
                                });
                                return;    
                            }
                            else{
                                this.transactionSubject.next({
                                    transaction_id: transaction_id,
                                    status: "cancelled"
                                });
                                return;    
                            }
                        }
                        else {
                            this.transactionSubject.next({
                                transaction_id: transaction_id,
                                status: "failed"
                            });
                            return;
                        }
                    }
                }
                else {
                    this.transactionSubject.next({
                        transaction_id: this.current_transaction_id,
                        status: "service_unavailable"
                    });
                    this.clearCurrentTransaction();
                }
            })
            .catch((error) => {
                this.transactionSubject.next({
                    transaction_id: this.current_transaction_id,
                    status: "service_unavailable",
                });
                this.clearCurrentTransaction();
            });
        })
        .catch((error) => {
            this.transactionSubject.next({
                transaction_id: this.current_transaction_id,
                status: "service_unavailable"
            });
            this.clearCurrentTransaction();
        });
    }

    private schedulePolling(response, resolve, reject) {
        if(response && response.data && response.data.PollingUrl) {
            this.current_transaction_id = response.transactionId;
            this.polling_url = response.data.PollingUrl;
            setTimeout(() => {
                // If 15 seconds have passed and transaction is still being handled, maybe pusher or airsquare are down. Poll directly
                if(this.processing_charge && this.current_transaction_id === response.transactionId) {
                    this.polling_interval_id = setInterval(() => this.pollTransaction(this.polling_url), 2300);
                }
            },15000);
            resolve(this.current_transaction_id);
        }
        else{
            this.clearCurrentTransaction();
            reject("");
        }
    }

    async chargeCard(amount:number):Promise<string> {
        return new Promise((resolve, reject) => {
            this.processing_charge = true;

            this.apiService.chargeSmartpayTerminal(this.current_register_id, amount).then(
                (response) => {
                    this.schedulePolling(response, resolve, reject)
                }
            ).catch((error) => {
                this.processing_charge = false;
                if(error.error.errors[0] == "register_not_paired") {
                    this.forgetReader();
                    reject("register_not_paired");
                } else if(error.error.errors[0] == "reader_busy") {
                    reject("reader_busy")
                } else {
                    reject("service_unavailable");
                }
            });                
        });
    }

    async refundCard(amount:number):Promise<string> {
        return new Promise((resolve, reject) => {
            this.processing_charge = true;

            this.apiService.refundSmartpayTerminal(this.current_register_id, amount).then(
                (response) => this.schedulePolling(response, resolve, reject)
            ).catch((error) => {
                this.processing_charge = false;
                if(error.error.errors[0] == "register_not_paired") {
                    this.forgetReader();
                    reject("register_not_paired");
                } else if(error.error.errors[0] == "reader_busy") {
                    reject("reader_busy")
                } else {
                    reject("service_unavailable");
                }
            });                
        });
    }
    
    async pairReader(pairing_code: string):Promise<'success' | 'failed' | 'invalid_pairing_code'> {
        return new Promise((resolve, reject) => {
            pairing_code = pairing_code.trim();
            this.apiService.pairSmartpayTerminal(this.current_register_id, pairing_code).then((response) => {
                if(response.result === "success"){
                    this.storageService.set("smartpay_reader", true);
                    resolve("success");
                }
            }).catch((error) => {
                if(error.error.errors[0] == 'invalid_pairing_code') {
                    reject("invalid_pairing_code");
                } else {
                    reject("failed");
                }
            });
                
        });
    }

    async forgetReader() {
        this.storageService.set("smartpay_reader", false);
    }
}
